import * as Yup from 'yup'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import InfoIcon from '@mui/icons-material/Info'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import { ChangePasswordByUserRequest } from '@professocialApi/professocial-core-frontend-api/apis/AutorisationROApi'
import { changePasswordByUserInitiated } from '../../../store/user/user.actions'
import { makeStyles } from '@mui/styles'
import TextFieldComponent from '@professocial/professocialFormComponents/FormComponents/TextFieldComponent'
import TooltipComponent from '@professocial/professocialFormComponents/FormComponents/TooltipComponent'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import PreloaderButton from '@professocial/professocialFormComponents/FormComponents/PreloaderButton'

import { ActionCall } from '@professocial/professocialTypes'
import { LoginRootState } from '../../../store/loginRoot.reducer'
import { namedRequestData } from '../../../store/common/common.selectors'
import { Typography } from '@mui/material'


type ResetPasswordFormValues = {
  password?: string;
  confirmPassword?: string;
};

type ResetPasswordProps = {
  token: string;
  onClose: (isOpen: boolean) => void;
};

const useStyles = makeStyles(() => ({
  invalidText: {
    color: '#d32f2f',
  },
  tooltip: {
    position: 'absolute',
    right: -34,
    top: 15,
  },
}))

const ResetPassword: React.FC<ResetPasswordProps> = ({ token, onClose }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const dispatchChangePassword = useCallback((params: ChangePasswordByUserRequest) => {
    dispatch(changePasswordByUserInitiated(params))
  }, [dispatch])

  const statusOnResetPasswordRequest: ActionCall | undefined = useSelector((state: LoginRootState) => namedRequestData(state, changePasswordByUserInitiated.type))

  const validationSchema = Yup.object().shape({
    password: Yup.string().matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$%\-@_]).{6,20}$/,
      { message: t('coreSettings:descPass'), excludeEmptyString: true }
    ),
    confirmPassword: Yup.string().oneOf(
      [Yup.ref('password'), null],
      t('accountsCommon:passwordsError')
    ).required(t('accountsCommon:passwordsError'))
  })

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ResetPasswordFormValues>({
    mode: 'all',
    resolver: yupResolver(validationSchema),
  })

  const handleResetPassword = (data: ResetPasswordFormValues) => {
    if (data.password && data.confirmPassword) {
      dispatchChangePassword({
        passwordRest: {
          passwordOne: data.password,
          passwordTwo: data.confirmPassword,
          accessToken: token,
        },
      })
    }
  }

  return (
    <Stack sx={{ m: 5 }} spacing={2} textAlign="center">
      {!statusOnResetPasswordRequest?.status && (
        <>
          <Box sx={{ position: 'relative' }}>
            <TextFieldComponent
              {...register('password')}
              id="password"
              label={t('accountsCommon:password')}
              type="password"
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{ autoComplete: 'new-password' }}
              fullWidth
              required
            />
            <Box className={classes.tooltip}>
              <TooltipComponent
                title={t('login:passwordInfo')}
                type="html"
                child={<InfoIcon color="primary" />}
                placement="right"
                arrow
              />
            </Box>
          </Box>
          <Box>
            <TextFieldComponent
              {...register('confirmPassword')}
              id="confirmPassword"
              label={t('accountsCommon:passwordAgain')}
              type="password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{ autoComplete: 'new-password' }}
              fullWidth
              required
            />
          </Box>
        </>
      )}
      {statusOnResetPasswordRequest?.status === 'success' && (
        <>
          <Typography variant="h2">{t('login:tokenAcceptedHeader')}</Typography>
          <Typography>{t('login:tokenAccepted')}</Typography>
        </>
      )}
      {statusOnResetPasswordRequest?.status === 'failed' && (
        <>
          <Typography variant="h2">{t('login:tokenInvalidErrorHeader')}</Typography>
          <Typography>{t('login:tokenInvalidError')}</Typography>
        </>
      )}
      <Stack direction="row" justifyContent="center" spacing={2} sx={{ pt: 2 }}>
        <ButtonComponent
          variant="text"
          startIcon={<KeyboardDoubleArrowLeftIcon />}
          onClick={() => onClose(false)}
          color="secondary"
          text={t('login:cancel')}
        />
        {!['success', 'failed'].includes(statusOnResetPasswordRequest?.status || '') && (
          <PreloaderButton
            position="left"
            loading={statusOnResetPasswordRequest?.inProgress}
            disableButton={!isDirty || statusOnResetPasswordRequest?.inProgress}
            color="primary"
            text={t('login:passwordSave')}
            onClick={handleSubmit(handleResetPassword)}
          />
        )}
      </Stack>
    </Stack>
  )
}

export default ResetPassword
